import { PageHeader, LoadingState, DataTable, ActionToolbar, EmptyState, FilterBar } from '../components/AdminPageUI';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Container, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  AutoStories as LogbookIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/Api';

const emptyForm = {
  activityDate: new Date().toISOString().slice(0, 10),
  description: '',
};

const emptyFilters = {
  search: '',
  dateFrom: '',
  dateTo: '',
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export default function SchoolLogbookPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filters, setFilters] = useState(emptyFilters);

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(Boolean).length,
    [filters],
  );

  const loadEntries = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value !== ''),
      );
      const res = await api.get('/school-logbook', { params });
      setEntries(res.data.entries || []);
      setSummary(res.data.summary || { total: 0 });
    } catch (err) {
      console.error('Failed to load school logbook:', err);
      const message = err.response?.data?.message || 'Failed to load school logbook entries.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const openCreateDialog = () => {
    setEditingEntry(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setForm({
      activityDate: entry.activityDate ? new Date(entry.activityDate).toISOString().slice(0, 10) : emptyForm.activityDate,
      description: entry.description || '',
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingEntry(null);
    setForm(emptyForm);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!form.description.trim()) {
      toast.warning('Log details are required.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        activityDate: form.activityDate,
        description: form.description.trim(),
      };

      if (editingEntry?._id) {
        await api.put(`/school-logbook/${editingEntry._id}`, payload);
        toast.success('Logbook entry updated.');
      } else {
        await api.post('/school-logbook', payload);
        toast.success('Logbook entry recorded.');
      }

      closeDialog();
      loadEntries();
    } catch (err) {
      console.error('Failed to save school logbook entry:', err);
      toast.error(err.response?.data?.message || 'Failed to save logbook entry.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    const confirmed = window.confirm(`Delete the logbook entry from ${formatDate(entry.activityDate)}?`);
    if (!confirmed) return;

    try {
      await api.delete(`/school-logbook/${entry._id}`);
      toast.success('Logbook entry deleted.');
      loadEntries();
    } catch (err) {
      console.error('Failed to delete school logbook entry:', err);
      toast.error(err.response?.data?.message || 'Failed to delete logbook entry.');
    }
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
  };

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      <PageHeader title={<>School Logbook</>} description={<>Record the date and details of school activities</>} actions={<>
        <Button variant="outlined" startIcon={<SearchIcon />} onClick={loadEntries} disabled={loading} fullWidth={isMobile}>
                  Refresh
                </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog} fullWidth={isMobile}>
                  New Entry
                </Button>
      </>}></PageHeader>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <FilterBar sx={{ mb: 4, boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: isMobile ? 2 : 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'flex-start' : 'center'} spacing={1}>
              <Box>
                <Typography variant="h6" fontWeight="600" sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
                  Filters
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Search details or filter entries by date.
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                {summary.total || 0} total entries
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid size={{ xs: 12, md: 6 }}   >
                <TextField
                  fullWidth
                  size={isMobile ? 'small' : 'medium'}
                  label="Search log details"
                  value={filters.search}
                  onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}    >
                <TextField
                  fullWidth
                  size={isMobile ? 'small' : 'medium'}
                  type="date"
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  value={filters.dateFrom}
                  onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}    >
                <TextField
                  fullWidth
                  size={isMobile ? 'small' : 'medium'}
                  type="date"
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  value={filters.dateTo}
                  onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12 }}  >
                <ActionToolbar direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="flex-end">
                  <Button variant="outlined" onClick={resetFilters} disabled={activeFilterCount === 0} fullWidth={isMobile}>
                    Clear Filters
                  </Button>
                  <Button variant="contained" onClick={loadEntries} disabled={loading} fullWidth={isMobile}>
                    Apply Filters
                  </Button>
                </ActionToolbar>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </FilterBar>

      <Card sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: isMobile ? 2 : 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" fontWeight="600" sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
              Logbook Entries ({entries.length})
            </Typography>
          </Box>

          {loading ? (
            <LoadingState />
          ) : entries.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <LogbookIcon sx={{ fontSize: isMobile ? 48 : 64, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>No logbook entries found</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Record the first logbook entry for the school.</Typography>
              <Button variant="outlined" onClick={openCreateDialog}>Create Entry</Button>
            </Box>
          ) : isMobile ? (
            <Box sx={{ p: 2 }}>
              <Stack spacing={2}>
                {entries.map((entry) => (
                  <Card key={entry._id} sx={{ boxShadow: 2, border: `1px solid ${theme.palette.divider}` }}>
                    <CardContent sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 800 }}>
                          {formatDate(entry.activityDate)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                          {entry.description}
                        </Typography>
                        <ActionToolbar direction="row" spacing={1}>
                          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEditDialog(entry)} sx={{ flex: 1 }}>Edit</Button>
                          <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(entry)} sx={{ flex: 1 }}>Delete</Button>
                        </ActionToolbar>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          ) : (
            <DataTable sx={{ width: '100%', overflow: 'auto', maxHeight: isTablet ? '60vh' : 'none' }}>
              <Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
                <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: 180 }}>DATE</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>DETAILS</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: 120, textAlign: 'center' }}>ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
{entries.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                  {entries.map((entry) => (
                    <TableRow
                      key={entry._id}
                      sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) }, transition: 'background-color 0.5s' }}
                    >
                      <TableCell sx={{ py: 2, fontWeight: 700 }}>{formatDate(entry.activityDate)}</TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                          {entry.description}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2, textAlign: 'center' }}>
                        <IconButton size="small" color="primary" onClick={() => openEditDialog(entry)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(entry)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </DataTable>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2, m: isMobile ? 0 : 'auto' } }}>
        <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <LogbookIcon />
          {editingEntry ? 'Edit Logbook Entry' : 'New Logbook Entry'}
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3, mt: 2 }}>
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12 }}  >
              <TextField
                fullWidth
                type="date"
                label="Date"
                InputLabelProps={{ shrink: true }}
                value={form.activityDate}
                onChange={(e) => handleFormChange('activityDate', e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}  >
              <TextField
                fullWidth
                multiline
                minRows={6}
                label="Details"
                value={form.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 3 }}>
          <Button onClick={closeDialog} variant="outlined" fullWidth={isMobile}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving} fullWidth={isMobile}>
            {saving ? 'Saving...' : editingEntry ? 'Update Entry' : 'Save Entry'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
